import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Badge, Tabs, Tab } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { leaveAPI } from '../services/api';
import { formatDateIST } from '../utils/dateFormatter';
import Swal from 'sweetalert2';
import { FaUmbrellaBeach, FaPlus, FaCheck, FaTimes, FaCalendarAlt } from 'react-icons/fa';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Pagination from '../components/Pagination';
import DOMPurify from 'dompurify';

const Leaves = () => {
  const { isAdmin, isHR, user } = useAuth();
  const [myLeaves, setMyLeaves] = useState([]);
  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [allLeaves, setAllLeaves] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    leave_type: 'casual',
    start_date: '',
    end_date: '',
    is_half_day: false,
    reason: ''
  });
  const [myLeavesPage, setMyLeavesPage] = useState(1);
  const [pendingLeavesPage, setPendingLeavesPage] = useState(1);
  const [allLeavesPage, setAllLeavesPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    fetchLeaves();
  }, []);

  const fetchLeaves = async () => {
    try {
      const myLeavesRes = await leaveAPI.getMyLeaves();
      setMyLeaves(myLeavesRes.data);

      if (user?.manager_id || isAdmin || isHR) {
        const pendingRes = await leaveAPI.getPendingLeaves();
        setPendingLeaves(pendingRes.data);
      }

      if (isAdmin || isHR) {
        const allLeavesRes = await leaveAPI.getAllLeaves();
        setAllLeaves(allLeavesRes.data);
      }
    } catch (error) {
      console.error('Error fetching leaves:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // If half-day is selected, set end_date to be same as start_date
    const submitData = {
      ...formData,
      end_date: formData.is_half_day ? formData.start_date : formData.end_date
    };

    try {
      await leaveAPI.create(submitData);
      setShowModal(false);
      setFormData({ leave_type: 'casual', start_date: '', end_date: '', is_half_day: false, reason: '' });
      fetchLeaves();
      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Leave request submitted!',
        confirmButtonColor: '#635bff',
        timer: 2000,
        showConfirmButton: false
      });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.detail || 'Error submitting leave request',
        confirmButtonColor: '#635bff'
      });
    }
  };

  const handleApprove = async (id) => {
    try {
      await leaveAPI.updateStatus(id, { status: 'approved' });
      fetchLeaves();
      Swal.fire({
        icon: 'success',
        title: 'Approved!',
        text: 'Leave has been approved.',
        confirmButtonColor: '#635bff',
        timer: 2000,
        showConfirmButton: false
      });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error approving leave',
        confirmButtonColor: '#635bff'
      });
    }
  };

  const handleReject = async (id) => {
    const result = await Swal.fire({
      title: 'Reject Leave',
      input: 'textarea',
      inputLabel: 'Reason for rejection',
      inputPlaceholder: 'Enter reason for rejection...',
      showCancelButton: true,
      confirmButtonColor: '#635bff',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Reject',
      inputValidator: (value) => {
        if (!value) {
          return 'Please provide a reason!';
        }
      }
    });

    if (result.isConfirmed) {
      try {
        await leaveAPI.updateStatus(id, { status: 'rejected', rejection_reason: result.value });
        fetchLeaves();
        Swal.fire({
          icon: 'success',
          title: 'Rejected!',
          text: 'Leave has been rejected.',
          confirmButtonColor: '#635bff',
          timer: 2000,
          showConfirmButton: false
        });
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Error rejecting leave',
          confirmButtonColor: '#635bff'
        });
      }
    }
  };

  const handleCancel = async (id) => {
    const result = await Swal.fire({
      title: 'Cancel Leave Request?',
      text: "This action cannot be undone",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, cancel it!'
    });

    if (result.isConfirmed) {
      try {
        await leaveAPI.cancel(id);
        fetchLeaves();
        Swal.fire({
          icon: 'success',
          title: 'Cancelled!',
          text: 'Leave request has been cancelled.',
          confirmButtonColor: '#635bff',
          timer: 2000,
          showConfirmButton: false
        });
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.detail || 'Error cancelling leave',
          confirmButtonColor: '#635bff'
        });
      }
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: '#fef3c7', color: '#92400e', text: 'Pending' },
      approved: { bg: '#d1fae5', color: '#065f46', text: 'Approved' },
      rejected: { bg: '#fee2e2', color: '#991b1b', text: 'Rejected' },
      cancelled: { bg: '#f3f4f6', color: '#6b7280', text: 'Cancelled' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span style={{
        padding: '4px 12px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: '500',
        backgroundColor: config.bg,
        color: config.color
      }}>
        {config.text}
      </span>
    );
  };

  const [activeTab, setActiveTab] = useState('my-leaves');

  return (
    <>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '32px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <h1 style={{
          fontSize: '32px',
          fontWeight: '700',
          color: '#0a0a0a',
          letterSpacing: '-0.02em',
          margin: 0
        }}>
          Leave Management
        </h1>
        <button
          onClick={() => setShowModal(true)}
          style={{
            padding: '12px 24px',
            borderRadius: '8px',
            backgroundColor: '#635bff',
            color: 'white',
            border: 'none',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
          }}
        >
          <FaPlus size={14} />
          Apply for Leave
        </button>
      </div>

      {/* Tabs */}
      <div style={{
        marginBottom: '24px',
        borderBottom: '2px solid #e5e7eb'
      }}>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => setActiveTab('my-leaves')}
            style={{
              padding: '12px 24px',
              border: 'none',
              backgroundColor: 'transparent',
              color: activeTab === 'my-leaves' ? '#635bff' : '#6b7280',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              borderBottom: activeTab === 'my-leaves' ? '2px solid #635bff' : '2px solid transparent',
              marginBottom: '-2px',
              transition: 'all 0.2s ease'
            }}
          >
            My Leaves
          </button>
          {pendingLeaves.length > 0 && (
            <button
              onClick={() => setActiveTab('pending')}
              style={{
                padding: '12px 24px',
                border: 'none',
                backgroundColor: 'transparent',
                color: activeTab === 'pending' ? '#635bff' : '#6b7280',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                borderBottom: activeTab === 'pending' ? '2px solid #635bff' : '2px solid transparent',
                marginBottom: '-2px',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              Pending Approvals
              <span style={{
                padding: '2px 8px',
                borderRadius: '12px',
                backgroundColor: '#635bff',
                color: 'white',
                fontSize: '12px',
                fontWeight: '600'
              }}>
                {pendingLeaves.length}
              </span>
            </button>
          )}
          {(isAdmin || isHR) && (
            <button
              onClick={() => setActiveTab('all-leaves')}
              style={{
                padding: '12px 24px',
                border: 'none',
                backgroundColor: 'transparent',
                color: activeTab === 'all-leaves' ? '#635bff' : '#6b7280',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                borderBottom: activeTab === 'all-leaves' ? '2px solid #635bff' : '2px solid transparent',
                marginBottom: '-2px',
                transition: 'all 0.2s ease'
              }}
            >
              All Leaves
            </button>
          )}
        </div>
      </div>

      {/* My Leaves Tab */}
      {activeTab === 'my-leaves' && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '20px 24px',
            borderBottom: '1px solid #e5e7eb',
            backgroundColor: '#faf9ff'
          }}>
            <h5 style={{
              margin: 0,
              fontSize: '18px',
              fontWeight: '600',
              color: '#0a0a0a',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <FaUmbrellaBeach style={{ color: '#635bff' }} />
              My Leave Requests
            </h5>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <Table hover responsive style={{ margin: 0 }}>
              <thead style={{ backgroundColor: '#faf9ff' }}>
                <tr>
                  <th style={{
                    padding: '16px 24px',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid #e5e7eb'
                  }}>Type</th>
                  <th style={{
                    padding: '16px 24px',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid #e5e7eb'
                  }}>Start Date</th>
                  <th style={{
                    padding: '16px 24px',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid #e5e7eb'
                  }}>End Date</th>
                  <th style={{
                    padding: '16px 24px',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid #e5e7eb'
                  }}>Status</th>
                  <th style={{
                    padding: '16px 24px',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid #e5e7eb'
                  }}>Reason</th>
                  <th style={{
                    padding: '16px 24px',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid #e5e7eb'
                  }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {myLeaves.length === 0 ? (
                  <tr>
                    <td colSpan="6" style={{
                      padding: '60px 20px',
                      textAlign: 'center',
                      color: '#9ca3af',
                      fontSize: '14px'
                    }}>
                      <div style={{ fontSize: '48px', marginBottom: '16px' }}>🏖️</div>
                      <div style={{ fontSize: '16px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                        No leave requests
                      </div>
                      <div>Your leave requests will appear here</div>
                    </td>
                  </tr>
                ) : (
                  (() => {
                    const indexOfLastItem = myLeavesPage * itemsPerPage;
                    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
                    const currentItems = myLeaves.slice(indexOfFirstItem, indexOfLastItem);

                    return currentItems.map((leave) => (
                      <tr key={leave.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{
                          padding: '16px 24px',
                          fontSize: '14px',
                          fontWeight: '500',
                          color: '#0a0a0a',
                          textTransform: 'capitalize'
                        }}>
                          {leave.leave_type}
                          {leave.is_half_day && (
                            <span style={{
                              marginLeft: '8px',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '11px',
                              fontWeight: '600',
                              backgroundColor: '#dbeafe',
                              color: '#1e40af'
                            }}>
                              Half Day
                            </span>
                          )}
                        </td>
                        <td style={{
                          padding: '16px 24px',
                          fontSize: '14px',
                          color: '#374151'
                        }}>
                          {formatDateIST(leave.start_date)}
                        </td>
                        <td style={{
                          padding: '16px 24px',
                          fontSize: '14px',
                          color: '#374151'
                        }}>
                          {leave.is_half_day ? '-' : formatDateIST(leave.end_date)}
                        </td>
                        <td style={{
                          padding: '16px 24px'
                        }}>
                          {getStatusBadge(leave.status)}
                        </td>
                        <td style={{
                          padding: '16px 24px',
                          fontSize: '14px',
                          color: '#374151'
                        }}>
                          <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(leave.reason) }} />
                        </td>
                        <td style={{
                          padding: '16px 24px'
                        }}>
                          {(leave.status === 'pending' || leave.status === 'approved') && (
                            <button
                              onClick={() => handleCancel(leave.id)}
                              style={{
                                padding: '6px 12px',
                                borderRadius: '6px',
                                backgroundColor: '#ef4444',
                                color: 'white',
                                border: 'none',
                                fontSize: '13px',
                                fontWeight: '500',
                                cursor: 'pointer'
                              }}
                            >
                              Cancel
                            </button>
                          )}
                        </td>
                      </tr>
                    ));
                  })()
                )}
              </tbody>
            </Table>
          </div>
          {myLeaves.length > 0 && (
            <Pagination
              currentPage={myLeavesPage}
              totalPages={Math.ceil(myLeaves.length / itemsPerPage)}
              onPageChange={setMyLeavesPage}
              itemsPerPage={itemsPerPage}
              totalItems={myLeaves.length}
            />
          )}
        </div>
      )}

      {/* Pending Approvals Tab */}
      {activeTab === 'pending' && pendingLeaves.length > 0 && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '20px 24px',
            borderBottom: '1px solid #e5e7eb',
            backgroundColor: '#faf9ff'
          }}>
            <h5 style={{
              margin: 0,
              fontSize: '18px',
              fontWeight: '600',
              color: '#0a0a0a',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <FaCalendarAlt style={{ color: '#635bff' }} />
              Pending Approvals
            </h5>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <Table hover responsive style={{ margin: 0 }}>
              <thead style={{ backgroundColor: '#faf9ff' }}>
                <tr>
                  <th style={{
                    padding: '16px 24px',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid #e5e7eb'
                  }}>Employee</th>
                  <th style={{
                    padding: '16px 24px',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid #e5e7eb'
                  }}>Type</th>
                  <th style={{
                    padding: '16px 24px',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid #e5e7eb'
                  }}>Start Date</th>
                  <th style={{
                    padding: '16px 24px',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid #e5e7eb'
                  }}>End Date</th>
                  <th style={{
                    padding: '16px 24px',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid #e5e7eb'
                  }}>Reason</th>
                  <th style={{
                    padding: '16px 24px',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid #e5e7eb'
                  }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const indexOfLastItem = pendingLeavesPage * itemsPerPage;
                  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
                  const currentItems = pendingLeaves.slice(indexOfFirstItem, indexOfLastItem);

                  return currentItems.map((leave) => (
                    <tr key={leave.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{
                        padding: '16px 24px',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#0a0a0a'
                      }}>
                        User #{leave.user_id}
                      </td>
                      <td style={{
                        padding: '16px 24px',
                        fontSize: '14px',
                        color: '#374151',
                        textTransform: 'capitalize'
                      }}>
                        {leave.leave_type}
                      </td>
                      <td style={{
                        padding: '16px 24px',
                        fontSize: '14px',
                        color: '#374151'
                      }}>
                        {formatDateIST(leave.start_date)}
                      </td>
                      <td style={{
                        padding: '16px 24px',
                        fontSize: '14px',
                        color: '#374151'
                      }}>
                        {formatDateIST(leave.end_date)}
                      </td>
                      <td style={{
                        padding: '16px 24px',
                        fontSize: '14px',
                        color: '#374151'
                      }}>
                        <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(leave.reason) }} />
                      </td>
                      <td style={{
                        padding: '16px 24px'
                      }}>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => handleApprove(leave.id)}
                            style={{
                              padding: '6px 12px',
                              borderRadius: '6px',
                              backgroundColor: '#10b981',
                              color: 'white',
                              border: 'none',
                              fontSize: '13px',
                              fontWeight: '500',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}
                          >
                            <FaCheck size={12} />
                            Approve
                          </button>
                          <button
                            onClick={() => handleReject(leave.id)}
                            style={{
                              padding: '6px 12px',
                              borderRadius: '6px',
                              backgroundColor: '#ef4444',
                              color: 'white',
                              border: 'none',
                              fontSize: '13px',
                              fontWeight: '500',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}
                          >
                            <FaTimes size={12} />
                            Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  ));
                })()}
              </tbody>
            </Table>
          </div>
          {pendingLeaves.length > 0 && (
            <Pagination
              currentPage={pendingLeavesPage}
              totalPages={Math.ceil(pendingLeaves.length / itemsPerPage)}
              onPageChange={setPendingLeavesPage}
              itemsPerPage={itemsPerPage}
              totalItems={pendingLeaves.length}
            />
          )}
        </div>
      )}

      {/* All Leaves Tab */}
      {activeTab === 'all-leaves' && (isAdmin || isHR) && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '20px 24px',
            borderBottom: '1px solid #e5e7eb',
            backgroundColor: '#faf9ff'
          }}>
            <h5 style={{
              margin: 0,
              fontSize: '18px',
              fontWeight: '600',
              color: '#0a0a0a',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <FaCalendarAlt style={{ color: '#635bff' }} />
              All Leaves
            </h5>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <Table hover responsive style={{ margin: 0 }}>
              <thead style={{ backgroundColor: '#faf9ff' }}>
                <tr>
                  <th style={{
                    padding: '16px 24px',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid #e5e7eb'
                  }}>Employee</th>
                  <th style={{
                    padding: '16px 24px',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid #e5e7eb'
                  }}>Type</th>
                  <th style={{
                    padding: '16px 24px',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid #e5e7eb'
                  }}>Start Date</th>
                  <th style={{
                    padding: '16px 24px',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid #e5e7eb'
                  }}>End Date</th>
                  <th style={{
                    padding: '16px 24px',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid #e5e7eb'
                  }}>Status</th>
                  <th style={{
                    padding: '16px 24px',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid #e5e7eb'
                  }}>Approved By</th>
                  <th style={{
                    padding: '16px 24px',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid #e5e7eb'
                  }}>Approved On</th>
                  <th style={{
                    padding: '16px 24px',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid #e5e7eb'
                  }}>Reason</th>
                </tr>
              </thead>
              <tbody>
                {allLeaves.length === 0 ? (
                  <tr>
                    <td colSpan="8" style={{
                      padding: '60px 20px',
                      textAlign: 'center',
                      color: '#9ca3af',
                      fontSize: '14px'
                    }}>
                      <div style={{ fontSize: '48px', marginBottom: '16px' }}>🏖️</div>
                      <div style={{ fontSize: '16px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                        No leave records
                      </div>
                      <div>Leave records will appear here</div>
                    </td>
                  </tr>
                ) : (
                  (() => {
                    const indexOfLastItem = allLeavesPage * itemsPerPage;
                    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
                    const currentItems = allLeaves.slice(indexOfFirstItem, indexOfLastItem);

                    return currentItems.map((leave) => (
                      <tr key={leave.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{
                          padding: '16px 24px',
                          fontSize: '14px',
                          fontWeight: '500',
                          color: '#0a0a0a'
                        }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <span style={{ fontWeight: '600' }}>
                              {leave.user?.full_name || `User #${leave.user_id}`}
                            </span>
                            {leave.user?.email && (
                              <span style={{ fontSize: '12px', color: '#6b7280' }}>
                                {leave.user.email}
                              </span>
                            )}
                          </div>
                        </td>
                        <td style={{
                          padding: '16px 24px',
                          fontSize: '14px',
                          color: '#374151',
                          textTransform: 'capitalize'
                        }}>
                          {leave.leave_type}
                          {leave.is_half_day && (
                            <span style={{
                              marginLeft: '8px',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '11px',
                              fontWeight: '600',
                              backgroundColor: '#dbeafe',
                              color: '#1e40af'
                            }}>
                              Half Day
                            </span>
                          )}
                        </td>
                        <td style={{
                          padding: '16px 24px',
                          fontSize: '14px',
                          color: '#374151'
                        }}>
                          {formatDateIST(leave.start_date)}
                        </td>
                        <td style={{
                          padding: '16px 24px',
                          fontSize: '14px',
                          color: '#374151'
                        }}>
                          {leave.is_half_day ? '-' : formatDateIST(leave.end_date)}
                        </td>
                        <td style={{
                          padding: '16px 24px'
                        }}>
                          {getStatusBadge(leave.status)}
                        </td>
                        <td style={{
                          padding: '16px 24px',
                          fontSize: '14px',
                          color: '#374151'
                        }}>
                          {leave.approved_by ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                              <span style={{ fontWeight: '500' }}>
                                {leave.approved_by.full_name}
                              </span>
                              <span style={{ fontSize: '12px', color: '#6b7280' }}>
                                {leave.approved_by.email}
                              </span>
                            </div>
                          ) : (
                            <span style={{ color: '#9ca3af' }}>-</span>
                          )}
                        </td>
                        <td style={{
                          padding: '16px 24px',
                          fontSize: '14px',
                          color: '#374151'
                        }}>
                          {leave.approval_date ? (
                            formatDateIST(leave.approval_date)
                          ) : (
                            <span style={{ color: '#9ca3af' }}>-</span>
                          )}
                        </td>
                        <td style={{
                          padding: '16px 24px',
                          fontSize: '14px',
                          color: '#374151'
                        }}>
                          <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(leave.reason) }} />
                        </td>
                      </tr>
                    ));
                  })()
                )}
              </tbody>
            </Table>
          </div>
          {allLeaves.length > 0 && (
            <Pagination
              currentPage={allLeavesPage}
              totalPages={Math.ceil(allLeaves.length / itemsPerPage)}
              onPageChange={setAllLeavesPage}
              itemsPerPage={itemsPerPage}
              totalItems={allLeaves.length}
            />
          )}
        </div>
      )}

      {/* Apply for Leave Sidebar */}
      <>
        {/* Overlay */}
        {showModal && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1040,
              transition: 'opacity 0.3s ease'
            }}
            onClick={() => setShowModal(false)}
          />
        )}

        {/* Sidebar */}
        <div
          style={{
            position: 'fixed',
            top: 0,
            right: showModal ? 0 : '-600px',
            width: '600px',
            height: '100vh',
            backgroundColor: 'white',
            boxShadow: '-2px 0 8px rgba(0, 0, 0, 0.1)',
            zIndex: 1050,
            transition: 'right 0.3s ease',
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto'
          }}
        >
          {/* Header */}
          <div style={{
            padding: '24px',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: 'white',
            position: 'sticky',
            top: 0,
            zIndex: 10
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '8px',
                backgroundColor: '#e8e5ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px'
              }}>
                🏖️
              </div>
              <h3 style={{
                margin: 0,
                fontSize: '20px',
                fontWeight: '600',
                color: '#0a0a0a'
              }}>
                Apply for Leave
              </h3>
            </div>
            <button
              onClick={() => setShowModal(false)}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                color: '#9ca3af',
                cursor: 'pointer',
                padding: '4px',
                lineHeight: 1
              }}
            >
              ×
            </button>
          </div>

          {/* Body */}
          <div style={{ flex: 1, padding: '24px' }}>
            <p style={{
              color: '#6b7280',
              fontSize: '14px',
              marginBottom: '24px',
              lineHeight: '1.6'
            }}>
              Submit your leave request by filling out the form below. Your manager will be notified for approval.
            </p>

            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-4">
                <Form.Label style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px',
                  display: 'block'
                }}>
                  Leave Type
                </Form.Label>
                <Form.Select
                  value={formData.leave_type}
                  onChange={(e) => setFormData({ ...formData, leave_type: e.target.value })}
                  style={{
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    padding: '12px 14px',
                    fontSize: '14px',
                    transition: 'border-color 0.2s ease'
                  }}
                >
                  <option value="casual">Casual Leave</option>
                  <option value="sick">Sick Leave</option>
                  <option value="annual">Annual Leave</option>
                  <option value="unpaid">Unpaid Leave</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Check
                  type="checkbox"
                  label="Half Day Leave"
                  checked={formData.is_half_day}
                  onChange={(e) => setFormData({
                    ...formData,
                    is_half_day: e.target.checked,
                    end_date: e.target.checked ? formData.start_date : formData.end_date
                  })}
                  style={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151'
                  }}
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px',
                  display: 'block'
                }}>
                  {formData.is_half_day ? 'Date' : 'Start Date'}
                </Form.Label>
                <Form.Control
                  required
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({
                    ...formData,
                    start_date: e.target.value,
                    end_date: formData.is_half_day ? e.target.value : formData.end_date
                  })}
                  style={{
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    padding: '12px 14px',
                    fontSize: '14px',
                    transition: 'border-color 0.2s ease'
                  }}
                />
              </Form.Group>

              {!formData.is_half_day && (
                <Form.Group className="mb-4">
                  <Form.Label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '8px',
                    display: 'block'
                  }}>
                    End Date
                  </Form.Label>
                  <Form.Control
                    required
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    style={{
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      padding: '12px 14px',
                      fontSize: '14px',
                      transition: 'border-color 0.2s ease'
                    }}
                  />
                </Form.Group>
              )}

              <Form.Group className="mb-4">
                <Form.Label style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px',
                  display: 'block'
                }}>
                  Reason
                </Form.Label>
                <ReactQuill
                  theme="snow"
                  value={formData.reason}
                  onChange={(value) => setFormData({ ...formData, reason: value })}
                  placeholder="Explain the reason for your leave..."
                  style={{
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    backgroundColor: 'white'
                  }}
                  modules={{
                    toolbar: [
                      ['bold', 'italic', 'underline'],
                      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                      ['clean']
                    ]
                  }}
                />
              </Form.Group>

              <div style={{
                display: 'flex',
                gap: '12px',
                paddingTop: '16px',
                borderTop: '1px solid #e5e7eb',
                marginTop: '24px'
              }}>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={{
                    flex: 1,
                    padding: '12px 20px',
                    borderRadius: '8px',
                    backgroundColor: '#f3f4f6',
                    color: '#374151',
                    border: '1px solid #d1d5db',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    flex: 1,
                    padding: '12px 20px',
                    borderRadius: '8px',
                    backgroundColor: '#635bff',
                    color: 'white',
                    border: 'none',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Submit Request
                </button>
              </div>
            </Form>
          </div>
        </div>
      </>
    </>
  );
};

export default Leaves;
